import React, { useEffect, useState } from "react";
import api, { extractErrorMessage } from "@/lib/api";
import { Database, Download, Trash2, Clock, Save, ShieldAlert } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const fmtSize = (b) => {
  if (b == null) return "—";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(2)} MB`;
};

const fmtDT = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("pt-BR", {
      dateStyle: "short",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
};

export default function Backup() {
  const [data, setData] = useState({ backups: [], retention_days: 30, next_scheduled: "", schedule_utc: "" });
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");

  const load = async () => {
    setError("");
    try {
      const { data: d } = await api.get("/admin/backup/list");
      setData(d);
    } catch (e) {
      setError(extractErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const create = async () => {
    setCreating(true);
    setMsg("");
    try {
      const { data: r } = await api.post("/admin/backup/create");
      setMsg(`✓ Backup criado: ${r.filename} (${r.documents} documentos, ${fmtSize(r.size_bytes)})`);
      await load();
    } catch (e) {
      setError(extractErrorMessage(e));
    } finally {
      setCreating(false);
    }
  };

  const download = (name) => {
    // Use fetch to include cookies then trigger a blob download (Response is a protected FastAPI file endpoint)
    fetch(`${BACKEND}/api/admin/backup/download/${name}`, { credentials: "include" })
      .then((r) => (r.ok ? r.blob() : Promise.reject(r.statusText)))
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = name;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      })
      .catch((e) => setError(String(e)));
  };

  const del = async (name) => {
    if (!window.confirm(`Excluir backup ${name}?`)) return;
    try {
      await api.delete(`/admin/backup/${name}`);
      await load();
    } catch (e) {
      setError(extractErrorMessage(e));
    }
  };

  return (
    <div>
      <div className="mb-6">
        <div className="overline mb-2 flex items-center gap-2">
          <Database size={13} /> Infraestrutura
        </div>
        <h1 className="font-display text-5xl font-black tracking-tight">
          BACKUP
        </h1>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="stat-card" data-testid="backup-count">
          <div className="overline mb-2">Backups armazenados</div>
          <div className="stat-number text-[#5AF78E]">{data.backups.length}</div>
        </div>
        <div className="stat-card" data-testid="backup-schedule">
          <div className="overline mb-2 flex items-center gap-1">
            <Clock size={11} /> Próximo automático
          </div>
          <div className="text-sm text-white mt-2">{fmtDT(data.next_scheduled)}</div>
          <div className="text-xs text-[var(--text-secondary)] mt-1">
            Diário às {data.schedule_utc} UTC
          </div>
        </div>
        <div className="stat-card" data-testid="backup-retention">
          <div className="overline mb-2">Retenção</div>
          <div className="stat-number text-white">{data.retention_days}</div>
          <div className="text-xs text-[var(--text-secondary)] mt-1">
            dias — mais antigos são apagados
          </div>
        </div>
      </div>

      <div className="card p-4 mb-6 flex items-start gap-3 border-l-4 border-[#FFCC00]">
        <ShieldAlert size={20} className="text-[#FFCC00] shrink-0 mt-0.5" />
        <div className="text-sm">
          <b>Backups locais.</b> Os arquivos ficam armazenados em{" "}
          <code className="text-xs bg-white/10 px-1">/app/backups</code> dentro
          do container. Para <b>segurança offsite</b>, baixe periodicamente os
          arquivos ou configure MongoDB Atlas Backup. Backups automáticos rodam{" "}
          <b>diariamente às {data.schedule_utc} UTC</b> e mantêm os últimos{" "}
          <b>{data.retention_days} dias</b>.
        </div>
      </div>

      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <button
          data-testid="backup-create-btn"
          className="btn-primary inline-flex items-center gap-2"
          onClick={create}
          disabled={creating}
        >
          <Save size={14} />
          {creating ? "Gerando..." : "CRIAR BACKUP AGORA"}
        </button>
        {msg && (
          <span
            data-testid="backup-msg"
            className="text-xs px-3 py-2 border text-[#5AF78E] border-[#5AF78E]/30 bg-[#5AF78E]/10"
          >
            {msg}
          </span>
        )}
      </div>

      {error && (
        <div className="text-sm text-[#C9A84C] border border-[#C9A84C]/30 bg-[#C9A84C]/10 px-3 py-2 mb-4">
          {error}
        </div>
      )}

      <div className="card overflow-x-auto">
        <table className="table-dense">
          <thead>
            <tr>
              <th>Arquivo</th>
              <th>Criado em</th>
              <th>Tamanho</th>
              <th className="text-right pr-4">Ações</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={4} className="text-center py-8 text-[var(--text-secondary)]">
                  Carregando...
                </td>
              </tr>
            ) : data.backups.length === 0 ? (
              <tr>
                <td colSpan={4} className="text-center py-8 text-[var(--text-secondary)]">
                  Nenhum backup ainda. Clique em <b>CRIAR BACKUP AGORA</b> para
                  gerar o primeiro.
                </td>
              </tr>
            ) : (
              data.backups.map((b) => (
                <tr key={b.filename} data-testid={`backup-row-${b.filename}`}>
                  <td className="font-mono text-xs">{b.filename}</td>
                  <td>{fmtDT(b.created_at)}</td>
                  <td className="tabular-nums">{fmtSize(b.size_bytes)}</td>
                  <td className="text-right pr-4">
                    <button
                      data-testid={`backup-download-${b.filename}`}
                      className="btn-ghost mr-1 !text-[#5AF78E]"
                      onClick={() => download(b.filename)}
                      title="Baixar"
                    >
                      <Download size={14} />
                    </button>
                    <button
                      data-testid={`backup-delete-${b.filename}`}
                      className="btn-ghost !text-[#C9A84C]"
                      onClick={() => del(b.filename)}
                      title="Excluir"
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
